import { Check, Trash, X } from "lucide-react";
import { useState } from "react";

import styles from "./ScoreItem.module.css";
import { useScoreItem } from "../../hooks/useScoreItem";
import EnumMap from "../../lib/enumMap";
import getClassificationClass from "../../lib/getClassificationClass";
import badgeStyles from "../../styles/BadgeGroups.module.css";
import cardStyles from "../../styles/Card.module.css";
import DeleteOverlay from "../DeleteOverlay/DeleteOverlay";

const EditableScoreItem = ({
    score,
    onDelete,
    onStatusUpdate,
    isPending
}) => {
    const [ isDeleting, setIsDeleting ] = useState(false);

    const { formattedDate } = useScoreItem(score);

    return (
        <div className={cardStyles.cardItem}>
            <div className={cardStyles.topRow}>
                <span className={cardStyles.date}>
                    { formattedDate }
                    { score.profile && (
                        <span>{` - ${ score.profile?.firstName } ${ score.profile?.lastName } (${ score.profile?.username })`}</span>
                    )}
                </span>

                <div className={badgeStyles.group}>
                    <div className={styles.classificationBadge}>
                        { EnumMap[score.status] }
                    </div>

                    { score.status !== "VERIFIED" && (
                        <button
                            onClick={() => onStatusUpdate(score.id, "VERIFIED")}
                            className={badgeStyles.badge} title={ "Approve Score" }
                        >
                            <Check />
                        </button>
                    )}

                    { score.status !== "REJECTED" && (
                        <button
                            onClick={() => onStatusUpdate(score.id, "REJECTED")}
                            className={badgeStyles.badge}
                            title={ "Reject Score" }
                        >
                            <X />
                        </button>
                    )}

                    <button
                        onClick={() => setIsDeleting(true)}
                        className={badgeStyles.badge}
                        title={ "Delete Score" }
                    >
                        <Trash />
                    </button>
                </div>

            </div>

            <div className={cardStyles.mainInfo}>
                <div className={styles.roundDetails}>
                    <h3>{score.roundName}</h3>
                    <p>{ EnumMap[score.bowstyle] } { EnumMap[score.ageCategory] } { EnumMap[score.sex] }, { EnumMap[score.competition] }, { EnumMap[score.venue] }</p>

                </div>

                <div className={styles.scoreDisplay}>
                    <span className={styles.actualScore}>{score.score}</span>
                    <span className={styles.maxScore}>/ {score.maxScore}</span>
                </div>
            </div>

            <div className={styles.statsGrid}>
                <div className={styles.statBlock}>
                    <label>Xs</label>
                    <span>{score.xs ?? "-"}</span>
                </div>

                <div className={styles.statBlock}>
                    <label>10s / 9s</label>
                    <span>{score.tens ?? "-"} / {score.nines ?? "-"}</span>
                </div>

                <div className={styles.statBlock}>
                    <label>Hits</label>
                    <span>{score.hits ?? "-"}</span>
                </div>
            </div>

            {(score.notes || score.journal) && (
                <div className={styles.notesGrid}>
                    {score.notes && (
                        <div className={styles.notesBlock}>
                            <label>Notes</label>
                            <span>{score.notes ?? "-"}</span>
                        </div>
                    )}

                    {score.journal && (
                        <div className={styles.notesBlock}>
                            <label>Journal</label>
                            <span>{score.journal ?? "-"}</span>
                        </div>
                    )}
                </div>
            )}

            <div className={cardStyles.footerRow}>
                <span className={`${styles.classificationBadge} ${getClassificationClass(score.classification)}`}>
                    Official Classification: { EnumMap[score.classification] || "Unclassified"}
                </span>

                { score.uncappedClassification && score.classification !== score.uncappedClassification && (
                    <span className={`${styles.classificationBadge} ${getClassificationClass(score.uncappedClassification)}`}>
                        Performance: { EnumMap[score.uncappedClassification] || "Unclassified"}
                    </span>
                )}

                {score.handicap && (
                    <span className={styles.handicap}>
                        Handicap: <strong>{Math.round(score.handicap)}</strong>
                    </span>
                )}
            </div>

            {isDeleting && (
                <DeleteOverlay
                    message="Remove this score from the user's records?"
                    onConfirm={() => onDelete(score.id)}
                    onCancel={() => setIsDeleting(false)}
                    isPending={isPending}
                />
            )}
        </div>
    );
};

export default EditableScoreItem;
